import React, { useState, useEffect, useContext, useMemo } from 'react';
import { getUsers, updateUser } from '../../services/apiServices';
import { OrganizationFacilityContext, useFeedbackContext } from '../../context';
import PaginatorV2 from '../../components/common/PaginatorV2';
import PaginationSkeleton from '../../components/common/PaginationSkeleton';
import SideBar from '../../components/organizations/SideBar';
import InviteSection from '../../components/organizations/InviteSection';
import UsersTable from '../../components/organizations/UsersTable';
import TableSkeleton from '../../components/common/TableSkeleton';
import FilterIcon from '../../components/common/FilterIcon';
import FilterSection from '../../components/organizations/FilterSection';
import ClearButton from '../../components/common/ClearButton';
import MainPageLayout from '../../components/common/MainPageLayout';
import usePagination from '../../hooks/usePagination';
import { serviceLogger } from '../../utils/serviceLogger';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });
  const [selectAll, setSelectAll] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState({});
  const [error, setError] = useState(null);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [showFilterSection, setShowFilterSection] = useState(false);
  const [isLoadingFilterFacilities, setIsLoadingFilterFacilities] = useState(false);
  const [selectedOrganizationFilter, setSelectedOrganizationFilter] = useState(null);
  const [selectedFacilityFilter, setSelectedFacilityFilter] = useState(null);
  const [facilitiesForSelectedOrganization, setFacilitiesForSelectedOrganization] = useState([]);

  const {
    organizationState: { allOrganizations, isLoadingOrganizations },
    facilityState: { allFacilities },
  } = useContext(OrganizationFacilityContext);

  const { showModal, showToast } = useFeedbackContext();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await getUsers();
        setUsers(data);
      } catch (err) {
        setError('Failed to fetch users');
      } finally {
        setIsLoadingUsers(false);
      }
    };

    fetchUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    let filtered = users;
    if (search) {
      const value = search.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.email.toLowerCase().includes(value) ||
          (user.name && user.name.toLowerCase().includes(value)),
      );
    }
    if (selectedOrganizationFilter && selectedOrganizationFilter.value) {
      filtered = filtered.filter((user) => user.organization === selectedOrganizationFilter.value);
    }
    if (selectedFacilityFilter && selectedFacilityFilter.value) {
      filtered = filtered.filter((user) => user.facility === selectedFacilityFilter.value);
    }
    return filtered;
  }, [users, search, selectedOrganizationFilter, selectedFacilityFilter]);

  useEffect(() => {
    if (selectedOrganizationFilter && selectedOrganizationFilter.value) {
      // Filter facilities that belong to the selected organization
      setIsLoadingFilterFacilities(true);
      const facilities = allFacilities.filter(
        (facility) => facility.organization === selectedOrganizationFilter.value,
      );
      setFacilitiesForSelectedOrganization(facilities);
      setIsLoadingFilterFacilities(false);
    } else {
      setFacilitiesForSelectedOrganization([]);
      setSelectedFacilityFilter(null);
      setIsLoadingFilterFacilities(false);
    }
  }, [selectedOrganizationFilter, allFacilities]);

  const recordsPerPage = 10;

  // Sort users according to the sortConfig state
  const sortedUsers = useMemo(() => {
    return [...filteredUsers].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredUsers, sortConfig]);

  const totalItems = sortedUsers.length;

  const {
    currentPage,
    totalPages,
    getPaginatedPageNumbers,
    handlePageChange,
    goToPrevPage,
    goToNextPage,
    resetPagination,
  } = usePagination(sortedUsers, recordsPerPage, totalItems, null);

  useEffect(() => {
    resetPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, selectedOrganizationFilter, selectedFacilityFilter]);

  if (error) {
    return <div>{error}</div>; // Show error message if occurs
  }

  // Functions related with users search, sort and pagination
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const highlightText = (text, searchTerm) => {
    if (!text) return '';
    if (!searchTerm) return text;
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    return text
      .toString()
      .split(regex)
      .map((part, index) => (regex.test(part) ? <mark key={index}>{part}</mark> : part));
  };

  const handleOrganizationFilterChange = (selectedOption) => {
    setSelectedOrganizationFilter(selectedOption);
    setSelectedFacilityFilter(null);
  };

  const handleFacilityFilterChange = (selectedOption) => {
    setSelectedFacilityFilter(selectedOption);
  };

  // Function to handle sort
  const handleSort = (columnKey) => {
    let direction = 'ascending';
    if (sortConfig.key === columnKey && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key: columnKey, direction });
  };

  // Obtain the users for the current page
  const indexOfLastUser = currentPage * recordsPerPage;
  const indexOfFirstUser = indexOfLastUser - recordsPerPage;
  const currentUsers = sortedUsers.slice(indexOfFirstUser, indexOfLastUser);

  // Handle selection of "All members" checkbox
  const handleSelectAllChange = (e) => {
    const isChecked = e.target.checked;
    setSelectAll(isChecked);
    const updatedSelectedUsers = {};
    currentUsers.forEach((user) => {
      updatedSelectedUsers[user.email] = isChecked;
    });
    setSelectedUsers((prevSelectedUsers) => ({
      ...prevSelectedUsers,
      ...updatedSelectedUsers,
    }));
  };

  // Handle selecting individual users
  const handleUserSelect = (e, email) => {
    const isChecked = e.target.checked;
    setSelectedUsers((prevSelectedUsers) => ({
      ...prevSelectedUsers,
      [email]: isChecked,
    }));
  };

  const handleFilterIconClick = () => {
    setShowFilterSection((prevState) => !prevState);
  };

  const handleClearFilters = () => {
    setSearch('');
    setSelectedOrganizationFilter(null);
    setSelectedFacilityFilter(null);
  };

  const confirmRoleChange = async (user, selectedRole) => {
    try {
      const updatedUserData = { role: selectedRole };
      const updatedUser = await updateUser(user.id, updatedUserData);
      setUsers((prevUsers) =>
        prevUsers.map((user) => (user.id === updatedUser.id ? updatedUser : user)),
      );

      showToast({
        variant: 'success',
        title: 'Role updated',
        copy: `The role of ${user.email} has been updated to ${selectedRole}.`,
      });
    } catch (error) {
      serviceLogger.error('Failed to update user role', error);
      showToast({
        variant: 'error',
        title: 'Error updating role',
        copy: `Could not update the role of ${user.email}. Please try again.`,
      });
    }
  };

  const handleRoleChange = (user, selectedRole) => {
    if (user.role === selectedRole) return;

    showModal({
      variant: 'warning',
      title: 'Confirm Role Change',
      copy: `Are you sure you want to change the role of ${user.email} to ${selectedRole}?`,
      buttonMessage: 'Confirm',
      onClickButton: () => confirmRoleChange(user, selectedRole),
    });
  };

  const renderPaginator = () => (
    <div className="mt-auto mb-2">
      <PaginatorV2
        getPaginatedPageNumbers={getPaginatedPageNumbers}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        total={totalPages}
        goToPrevPage={goToPrevPage}
        goToNextPage={goToNextPage}
      />
    </div>
  );

  const renderPaginationSkeleton = () => (
    <div className="mt-auto mb-2">
      <PaginationSkeleton />
    </div>
  );

  return (
    <MainPageLayout>
      <div className="flex flex-col w-full sm:w-full xl:flex-row xl:items-center xl:space-x-4">
        {/* Invite User Section */}
        <InviteSection />
      </div>

      {/* Main Content */}
      <div className="flex flex-wrap md:p-4 lg:p-4 lg:flex-row">
        {/* Side Bar */}
        <SideBar />

        {/* Users Table */}
        <div className="flex-grow p-4 w-full md:w-3/5">
          <div className="flex justify-end items-center pr-4 space-x-4">
            <ClearButton onClick={handleClearFilters} />
            <FilterIcon onClick={handleFilterIconClick} isOpen={showFilterSection} />
          </div>

          {showFilterSection && (
            <FilterSection
              search={search}
              handleSearchChange={handleSearchChange}
              allOrganizations={allOrganizations}
              selectedOrganizationFilter={selectedOrganizationFilter}
              handleOrganizationFilterChange={handleOrganizationFilterChange}
              selectedFacilityFilter={selectedFacilityFilter}
              handleFacilityFilterChange={handleFacilityFilterChange}
              facilitiesForSelectedOrganization={facilitiesForSelectedOrganization}
              isLoadingOrganizations={isLoadingOrganizations}
              isLoadingFilterFacilities={isLoadingFilterFacilities}
            />
          )}

          {isLoadingUsers ? (
            renderPaginationSkeleton()
          ) : (
            <div className="mb-4 sm:mb-0">{renderPaginator()}</div>
          )}

          {isLoadingUsers ? (
            <TableSkeleton columns={Array.from({ length: 4 })} rows={10} />
          ) : (
            <UsersTable
              currentUsers={currentUsers}
              selectedUsers={selectedUsers}
              selectAll={selectAll}
              handleUserSelect={handleUserSelect}
              handleSelectAllChange={handleSelectAllChange}
              handleSort={handleSort}
              sortConfig={sortConfig}
              highlightText={highlightText}
              search={search}
              allOrganizations={allOrganizations}
              allFacilities={allFacilities}
              onRoleChange={handleRoleChange}
            />
          )}

          {isLoadingUsers ? renderPaginationSkeleton() : renderPaginator()}
        </div>
      </div>
    </MainPageLayout>
  );
};

export default UserManagement;
